import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import initialize from './initialize';
import ClarityAPI from './API';
const BASE_CLARITY_URL = 'https://clarity.microsoft.com';
const ClarityContext = React.createContext(undefined);
export const ClarityProvider = ({ clarityId, ...rest }) => {
    const isInitialized = React.useRef(false);
    const [metadataValue, setMetadataValue] = React.useState(undefined);
    if (!isInitialized.current) {
        initialize(clarityId);
        isInitialized.current = true;
    }
    React.useEffect(() => {
        if (isInitialized.current) {
            ClarityAPI('metadata', (e) => {
                setMetadataValue(e);
            });
        }
    }, [isInitialized.current]);
    const start = React.useCallback((config) => {
        ClarityAPI('start', config);
    });
    const stop = () => {
        ClarityAPI('stop');
    };
    const pause = () => {
        ClarityAPI('pause');
    };
    const resume = () => {
        ClarityAPI('resume');
    };
    const upgrade = (key) => {
        ClarityAPI('upgrade', key);
    };
    const consent = () => {
        ClarityAPI('consent');
    };
    const event = (name, data) => {
        ClarityAPI('event', name, data);
    };
    const set = (key, value) => {
        ClarityAPI('set', key, value);
    };
    const identify = (userId, sessionId, pageId, userHint) => {
        ClarityAPI('identify', userId, sessionId, pageId, userHint);
    };
    const metadata = (callback, wait) => {
        ClarityAPI('metadata', callback, wait);
    };
    const signal = (callback) => {
        ClarityAPI('signal', callback);
    };
    const currentSessionUrl = React.useMemo(() => {
        if (!metadataValue?.projectId ||
            !metadataValue?.userId ||
            !metadataValue?.sessionId) {
            return undefined;
        }
        return `${BASE_CLARITY_URL}/player/${metadataValue.projectId}/${metadataValue.userId}/${metadataValue?.sessionId}`;
    }, [metadataValue]);
    const providerValue = React.useMemo(() => {
        return {
            start,
            stop,
            pause,
            resume,
            upgrade,
            consent,
            event,
            set,
            identify,
            metadata,
            signal,
            currentSessionId: metadataValue?.sessionId,
            clarityUserId: metadataValue?.userId,
            currentSessionUrl,
        };
    }, [
        start,
        stop,
        pause,
        resume,
        upgrade,
        consent,
        event,
        set,
        identify,
        metadata,
        signal,
        metadataValue,
        currentSessionUrl,
    ]);
    return (_jsx(ClarityContext.Provider, { value: providerValue, children: rest.children }));
};
export const useClarity = () => {
    const context = React.useContext(ClarityContext);
    if (context === undefined) {
        throw new Error('"useClarity" must be used within `ClarityProvider`.');
    }
    return context;
};
//# sourceMappingURL=data:application/json;base64,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